import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { uploadMultipartFast } from "@/lib/fastUpload";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, Upload, CheckCircle2, Camera, ArrowLeft, X } from "lucide-react";

/**
 * Photographer camera drop — mobile-first, passcode-gated, no login.
 *
 * URL: /drop (public)
 *
 * Flow:
 *  1. Enter passcode (verified against Settings.drop_passcode).
 *  2. Fill event / category / photographer name.
 *  3. Pick or shoot a video from phone camera roll.
 *  4. Chunked S3 multipart upload with progress bar.
 *  5. Success screen — go again or return.
 */

const CATEGORIES = [
  { id: "rodeo", label: "Rodeo" },
  { id: "jackpot", label: "Jackpot" },
  { id: "ranch_rodeo", label: "Ranch Rodeo" },
  { id: "bull_riding", label: "Bull Riding" },
  { id: "saddle_bronc", label: "Saddle Bronc" },
  { id: "barrel_racing", label: "Barrel Racing" },
  { id: "team_roping", label: "Team Roping" },
  { id: "tie_down_roping", label: "Tie-Down Roping" },
  { id: "steer_wrestling", label: "Steer Wrestling" },
  { id: "breakaway", label: "Breakaway" },
];

const probeDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = URL.createObjectURL(file);
    v.onloadedmetadata = () => {
      const d = v.duration;
      URL.revokeObjectURL(v.src);
      resolve(isFinite(d) ? d : 0);
    };
    v.onerror = () => resolve(0);
  });

export default function DropPage() {
  const [step, setStep] = useState("passcode"); // passcode | form | uploading | done
  const [passcode, setPasscode] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Form
  const [photographer, setPhotographer] = useState(() => localStorage.getItem("drop_photographer") || "");
  const [category, setCategory] = useState("barrel_racing");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);         // File[]
  const [fileStatuses, setFileStatuses] = useState([]); // {name,size,pct,phase,error,videoId}[]
  const fileRef = useRef(null);

  // Upload
  const [activeIdx, setActiveIdx] = useState(-1);
  const [uploadedCount, setUploadedCount] = useState(0);

  const verify = async (e) => {
    e?.preventDefault();
    if (!passcode.trim()) {
      toast.error("Enter the passcode");
      return;
    }
    setVerifying(true);
    try {
      await api.post("/drop/verify", { passcode: passcode.trim() });
      sessionStorage.setItem("drop_passcode", passcode.trim());
      setStep("form");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Wrong passcode";
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  const upload = async () => {
    if (!files.length) {
      toast.error("Pick at least one video");
      return;
    }
    if (!photographer.trim()) {
      toast.error("Your name is required");
      return;
    }
    if (!eventName.trim()) {
      toast.error("Event name is required");
      return;
    }
    localStorage.setItem("drop_photographer", photographer.trim());

    setStep("uploading");
    const statuses = files.map((f) => ({
      name: f.name, size: f.size, pct: 0, phase: "queued", error: "", videoId: null,
    }));
    setFileStatuses(statuses);
    setUploadedCount(0);

    const patch = (i, next) => setFileStatuses((prev) => {
      const copy = [...prev]; copy[i] = { ...copy[i], ...next }; return copy;
    });

    let okCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setActiveIdx(i);
      let videoId = null;
      try {
        patch(i, { phase: "probing" });
        const duration = await probeDuration(file);

        patch(i, { phase: "initiating" });
        const pc = passcode.trim();
        await uploadMultipartFast({
          file,
          onProgress: (pct) => patch(i, { pct: Math.round(pct) }),
          init: async () => {
            const res = await api.post("/drop/initiate-upload", {
              passcode: pc,
              filename: file.name,
              file_size: file.size,
              content_type: file.type || "video/mp4",
              category,
              duration,
              event_name: eventName.trim(),
              event_date: eventDate,
              notes: notes.trim(),
              photographer_name: photographer.trim(),
            });
            videoId = res.data.video_id;
            patch(i, { phase: "uploading", videoId });
            return res;
          },
          partUrl: (s, part_number) =>
            api.post("/drop/upload-part-url", { passcode: pc, video_id: s.video_id, part_number }),
          complete: (s, parts) => {
            patch(i, { phase: "finalizing" });
            return api.post("/drop/complete-upload", { passcode: pc, video_id: s.video_id, parts, duration });
          },
        });

        patch(i, { phase: "done", pct: 100 });
        okCount++;
        setUploadedCount(okCount);
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "Upload failed";
        patch(i, { phase: "failed", error: String(msg).slice(0, 160) });
        failCount++;
        if (videoId) api.post(`/videos/${videoId}/abort-upload`).catch(() => {});
      }
    }

    setActiveIdx(-1);
    if (okCount > 0 && failCount === 0) {
      setStep("done");
    } else if (okCount > 0) {
      toast.warning(`${okCount} uploaded · ${failCount} failed`);
      setStep("done");
    } else {
      toast.error("All uploads failed — check your connection", { duration: 10000 });
      setStep("form");
    }
  };

  const startAnother = () => {
    setFiles([]);
    setFileStatuses([]);
    setActiveIdx(-1);
    setUploadedCount(0);
    setNotes("");
    setEventName("");
    setStep("form");
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 font-sans" data-testid="drop-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="border-b border-stone-800 px-4 py-3 flex items-center gap-3 sticky top-0 bg-stone-950/95 backdrop-blur z-10">
        <Camera size={20} className="text-orange-500" />
        <div>
          <div className="text-sm font-display uppercase tracking-widest">Rodeo Cut Studio</div>
          <div className="text-[10px] text-stone-500 uppercase tracking-widest">Photographer drop</div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {step === "passcode" && (
          <form onSubmit={verify} className="space-y-4" data-testid="drop-passcode-step">
            <div>
              <h1 className="text-2xl font-display uppercase font-bold mb-1">Enter passcode</h1>
              <p className="text-stone-400 text-sm">Ask the team for the drop passcode.</p>
            </div>
            <input
              type="text"
              inputMode="text"
              autoFocus
              autoComplete="off"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 text-lg font-mono focus:outline-none focus:border-orange-500"
              data-testid="drop-passcode-field"
            />
            <button
              type="submit"
              disabled={verifying}
              className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white text-sm uppercase tracking-widest font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="drop-passcode-submit"
            >
              {verifying ? <Loader2 size={16} className="animate-spin" /> : null}
              {verifying ? "Checking…" : "Continue"}
            </button>
          </form>
        )}

        {step === "form" && (
          <div className="space-y-4" data-testid="drop-form-step">
            <div>
              <h1 className="text-xl font-display uppercase font-bold">Drop a video</h1>
              <p className="text-stone-400 text-xs">Fill in the details, then pick or shoot a video.</p>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">
                Your name <span className="text-orange-500">*</span>
              </label>
              <input
                type="text"
                value={photographer}
                onChange={(e) => setPhotographer(e.target.value)}
                placeholder="e.g. Alex Rivera"
                className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 focus:outline-none focus:border-orange-500"
                data-testid="drop-photographer-field"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">
                Event name <span className="text-orange-500">*</span>
              </label>
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g. Kindersley CCA Rodeo — Perf 2"
                className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 focus:outline-none focus:border-orange-500"
                data-testid="drop-event-name-field"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">Event date</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 focus:outline-none focus:border-orange-500"
                data-testid="drop-event-date-field"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 focus:outline-none focus:border-orange-500"
                data-testid="drop-category-field"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">Notes (optional)</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything the editor should know"
                className="w-full bg-stone-900 border border-stone-700 rounded-none px-4 py-3 focus:outline-none focus:border-orange-500"
                data-testid="drop-notes-field"
              />
            </div>

            <div className="pt-2">
              <input
                type="file"
                accept="video/*"
                multiple
                ref={fileRef}
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []);
                  if (picked.length) setFiles((prev) => [...prev, ...picked]);
                  // reset the input so re-picking the same file works
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="hidden"
                data-testid="drop-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className={`w-full py-6 border-2 border-dashed ${files.length ? "border-orange-500 bg-orange-950/20" : "border-stone-700 bg-stone-900/50"} text-center inline-flex flex-col items-center gap-2 hover:border-orange-500 transition-colors`}
                data-testid="drop-file-picker"
              >
                <Upload size={28} className={files.length ? "text-orange-500" : "text-stone-500"} />
                {files.length ? (
                  <>
                    <div className="text-sm font-medium">
                      {files.length} video{files.length === 1 ? "" : "s"} selected · {(files.reduce((a, f) => a + f.size, 0) / 1024 / 1024).toFixed(1)} MB total
                    </div>
                    <div className="text-[10px] text-stone-500 uppercase tracking-widest">
                      Tap to add more
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium">Pick or shoot videos</div>
                    <div className="text-[10px] text-stone-500 uppercase tracking-widest">
                      MP4, MOV, MPG · multi-select supported
                    </div>
                  </>
                )}
              </button>

              {files.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="drop-file-list">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}_${f.size}_${i}`}
                      className="flex items-center justify-between gap-2 bg-stone-900/60 border border-stone-800 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{f.name}</div>
                        <div className="text-[10px] text-stone-500 font-mono">
                          {(f.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                      <button
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-stone-500 hover:text-rose-400 shrink-0 p-1"
                        aria-label="Remove"
                        data-testid={`drop-file-remove-${i}`}
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              onClick={upload}
              disabled={!files.length}
              className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white text-sm uppercase tracking-widest font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="drop-upload-submit"
            >
              <Upload size={16} />
              {files.length <= 1 ? "Drop it" : `Drop ${files.length} videos`}
            </button>
          </div>
        )}

        {step === "uploading" && (
          <div className="space-y-4 py-4" data-testid="drop-uploading-step">
            <div className="flex items-center gap-3">
              <Loader2 size={20} className="animate-spin text-orange-500" />
              <div>
                <div className="text-sm font-medium">
                  Uploading {uploadedCount + (activeIdx >= 0 ? 1 : 0)} of {fileStatuses.length}
                </div>
                <div className="text-[10px] text-stone-500 uppercase tracking-widest">
                  Don&apos;t close this tab
                </div>
              </div>
            </div>

            <ul className="space-y-2" data-testid="drop-upload-list">
              {fileStatuses.map((s, i) => (
                <li
                  key={i}
                  className={`border ${s.phase === "done" ? "border-emerald-800 bg-emerald-950/20" : s.phase === "failed" ? "border-rose-800 bg-rose-950/20" : i === activeIdx ? "border-orange-700 bg-stone-900" : "border-stone-800 bg-stone-900/60"} px-3 py-2.5`}
                  data-testid={`drop-upload-item-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs truncate">{s.name}</div>
                      <div className="text-[10px] text-stone-500 font-mono">
                        {(s.size / 1024 / 1024).toFixed(1)} MB
                        {s.phase === "done" && " · uploaded"}
                        {s.phase === "failed" && ` · ${s.error}`}
                        {s.phase === "uploading" && ` · uploading…`}
                        {s.phase === "finalizing" && ` · finalizing…`}
                        {s.phase === "initiating" && ` · starting…`}
                        {s.phase === "probing" && ` · reading…`}
                        {s.phase === "queued" && ` · queued`}
                      </div>
                    </div>
                    <div className="text-sm font-mono text-orange-400 shrink-0 w-12 text-right">
                      {s.phase === "done" ? "✓" : s.phase === "failed" ? "✗" : `${s.pct}%`}
                    </div>
                  </div>
                  {s.phase !== "done" && s.phase !== "failed" && (
                    <div className="w-full h-1 bg-stone-800 mt-2">
                      <div
                        className="h-full bg-orange-500 transition-all"
                        style={{ width: `${s.pct}%` }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-6 py-8 text-center" data-testid="drop-done-step">
            <CheckCircle2 size={64} className="text-emerald-500 mx-auto" />
            <div>
              <h1 className="text-2xl font-display uppercase font-bold mb-2">
                {uploadedCount === 1 ? "Dropped!" : `${uploadedCount} dropped!`}
              </h1>
              <p className="text-stone-400 text-sm">
                Thanks {photographer}. The team has been notified — {uploadedCount === 1 ? "it's" : "they're"} in the Studio, ready to cut.
              </p>
              {fileStatuses.some((s) => s.phase === "failed") && (
                <div className="mt-3 text-xs text-rose-400" data-testid="drop-done-failed-summary">
                  {fileStatuses.filter((s) => s.phase === "failed").length} failed — try again from the drop page.
                </div>
              )}
            </div>
            <button
              onClick={startAnother}
              className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white text-sm uppercase tracking-widest font-medium inline-flex items-center justify-center gap-2"
              data-testid="drop-again-btn"
            >
              <Upload size={14} />
              Drop more
            </button>
            <button
              onClick={() => {
                setPasscode("");
                sessionStorage.removeItem("drop_passcode");
                setStep("passcode");
                setFiles([]);
                setFileStatuses([]);
              }}
              className="w-full py-2 text-xs text-stone-500 hover:text-stone-300 inline-flex items-center justify-center gap-1"
              data-testid="drop-signout-btn"
            >
              <ArrowLeft size={12} />
              Sign out
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
